"use client";

import { createSelector } from "@reduxjs/toolkit";
import {
  Check,
  ChevronDown,
  ChevronUp,
  Loader2,
  Star,
  StarHalf,
} from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useMemo, useState } from "react";
import { BiLogoWhatsapp } from "react-icons/bi";

import AddToCartButton from "@/src/features/cart/components/AddToCartButton";
import { cartApi, selectIsVariantInCart } from "@/src/features/cart/cartApi";
import { useCart } from "@/src/features/cart/hooks/useCart";
import { siteConfig } from "@/src/config/site";
import { formatDiscountLabel } from "@/src/lib/utils/discount";
import { cn } from "@/src/lib/utils/cn";
import { useAppSelector } from "@/src/store/hooks";

type InfoItem = {
  label: string;
  value: string;
};

type InfoGroup = {
  variantId?: number;
  price?: string;
  group: string;
  title?: string;
  items: InfoItem[];
};

type GroupedInfoItem =
  | {
      label: string;
      type: "text";
      value: string;
    }
  | {
      label: string;
      type: "color";
      colors: string[];
    };

type ProductDetailsPanelProps = {
  product: {
    variantId: number;
    title: string;
    description: string;
    categoryLabel: string;
    isNew: boolean;
    rating: number | null;
    reviewsCount: number;
    stock: number;
    isPublic: boolean;
    price: string;
    originalPrice: string;
    discountPercentage: string;
    sku: string;
  };
  isAvailable: boolean;
  availabilityLabel: string;
  infoGroups: InfoGroup[];
  showInfoGroups?: boolean;
  showSelectableGroups?: boolean;
  selectedVariantIdsOverride?: number[];
  hasSelectableInfoGroupsOverride?: boolean;
  isFullProductSelectedOverride?: boolean;
};

const primaryButtonClass =
  "inline-flex min-h-[3.5rem] w-full items-center justify-center gap-2 rounded bg-[#ee376a] px-5 py-3 text-base font-extrabold text-white shadow-[0_18px_38px_rgba(238,55,106,0.24)] transition hover:bg-[#dd2f61]";

const secondaryButtonClass =
  "inline-flex min-h-[3.5rem] w-full items-center justify-center gap-2 rounded border border-[#f59fc4] bg-white px-5 py-3 text-base font-extrabold text-[#ee376a] transition hover:bg-[#fff2f8]";

function formatCurrency(value: string) {
  const parsed = Number.parseFloat(value);

  if (Number.isNaN(parsed)) {
    return `${value} جنيه`;
  }

  return `${new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(parsed)} جنيه`;
}

function renderStars(rating: number | null, size = "h-4 w-4") {
  const normalizedRating = Math.max(0, Math.min(rating ?? 0, 5));
  const ratingInHalves = Math.round(normalizedRating * 2) / 2;

  return Array.from({ length: 5 }).map((_, index) => (
    <span key={index} className="relative inline-flex">
      {index + 1 <= ratingInHalves ? (
        <Star className={`${size} fill-current text-[#f5b301]`} />
      ) : index + 0.5 === ratingInHalves ? (
        <StarHalf className={`${size} fill-current text-[#f5b301]`} />
      ) : (
        <Star className={`${size} text-[#d9d2cb]`} />
      )}
    </span>
  ));
}

function isHexColor(value: string) {
  return /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(value.trim());
}

function extractHexColors(value: string) {
  return (
    value.match(/#(?:[0-9a-fA-F]{8}|[0-9a-fA-F]{6}|[0-9a-fA-F]{3})/g) ?? []
  ).map((color) => color.trim());
}

function isColorInfoItem(item: InfoItem) {
  const label = item.label.trim();
  const rawValue = item.value.trim();

  return (
    /لون|color/i.test(label) ||
    isHexColor(rawValue) ||
    extractHexColors(rawValue).length > 0
  );
}

function groupInfoItems(items: InfoItem[]): GroupedInfoItem[] {
  const textItems: GroupedInfoItem[] = [];
  const colorMap = new Map<string, string>();
  let colorLabel = "";

  items.forEach((item) => {
    if (!isColorInfoItem(item)) {
      textItems.push({
        label: item.label,
        type: "text",
        value: item.value,
      });
      return;
    }

    const colors = extractHexColors(item.value.trim());
    const fallbackColor = isHexColor(item.value.trim())
      ? [item.value.trim()]
      : [];

    if (!colorLabel) {
      colorLabel = item.label.trim();
    }

    [...colors, ...fallbackColor].forEach((color) => {
      colorMap.set(color.toLowerCase(), color);
    });
  });

  if (colorMap.size === 0) {
    return textItems;
  }

  return [
    {
      label: colorLabel || "الألوان",
      type: "color",
      colors: Array.from(colorMap.values()),
    },
    ...textItems,
  ];
}

function OptionValueCell({ item }: { item: GroupedInfoItem }) {
  if (item.type === "color") {
    return (
      <div className="flex min-w-0 flex-wrap items-center justify-start gap-2 text-start">
        <span className="flex flex-wrap items-center justify-start gap-1.5">
          {item.colors.map((color) => (
            <span
              key={color}
              className="inline-flex h-4 w-4 rounded-full border border-white shadow-[0_0_0_1px_rgba(238,55,106,0.24)] sm:h-5 sm:w-5"
              style={{ backgroundColor: color }}
              title={color}
            />
          ))}
        </span>
      </div>
    );
  }

  return (
    <span className="block min-w-0 break-words text-start text-xs font-bold leading-6 text-[#6f5b63] sm:text-sm">
      {item.value.trim()}
    </span>
  );
}

function OptionTableRow({
  item,
  index,
}: {
  item: GroupedInfoItem;
  index: number;
}) {
  return (
    <div
      className={cn(
        "grid min-h-[2.7rem] grid-cols-[minmax(6rem,0.85fr)_minmax(0,1.15fr)] items-stretch border-t border-[rgba(238,55,106,0.12)] text-right first:border-t-0",
        index % 2 === 0 ? "bg-white" : "bg-[#fffafb]",
      )}
    >
      <div className="flex items-center justify-start border-l border-[rgba(238,55,106,0.12)] px-3 py-2 text-xs font-extrabold text-[var(--color-ink)] sm:px-4 sm:text-sm">
        <span className="min-w-0 break-words">{item.label.trim()}</span>
      </div>
      <div className="flex min-w-0 items-center justify-start px-3 py-2 sm:px-4">
        <OptionValueCell item={item} />
      </div>
    </div>
  );
}

function OptionGroupDisplay({ group }: { group: InfoGroup }) {
  const [isOpen, setIsOpen] = useState(true);
  const groupedInfoItems = groupInfoItems(group.items);

  if (groupedInfoItems.length === 0) {
    return null;
  }

  return (
    <section className="overflow-hidden rounded-[1.05rem] border border-[rgba(238,55,106,0.14)] bg-[#fff8fb] shadow-[0_10px_24px_rgba(61,45,33,0.04)]">
      <button
        type="button"
        aria-expanded={isOpen}
        className="flex min-h-[3rem] w-full cursor-pointer items-center justify-between gap-3 border-b border-[rgba(238,55,106,0.12)] bg-[linear-gradient(180deg,#fff7fb_0%,#fff_100%)] px-3 py-2 sm:px-4"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-[rgba(238,55,106,0.18)] bg-white text-[#ee376a] shadow-[0_4px_10px_rgba(238,55,106,0.08)]">
          {isOpen ? (
            <ChevronUp className="h-3.5 w-3.5" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5" />
          )}
        </span>
        <h2 className="min-w-0 flex-1 break-words text-right text-sm font-extrabold text-[var(--color-ink)]">
          {group.group}
        </h2>
      </button>
      {isOpen ? (
        <div className="overflow-hidden bg-white">
          {groupedInfoItems.map((item, index) => (
            <OptionTableRow
              key={`${group.group}-${item.label}-${index}`}
              item={item}
              index={index}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
}

function SelectableGroupCard({
  group,
  isSelected,
  onToggle,
}: {
  group: InfoGroup;
  isSelected: boolean;
  onToggle: () => void;
}) {
  const visibleItems = groupInfoItems(group.items).slice(0, 3);

  return (
    <button
      type="button"
      aria-pressed={isSelected}
      onClick={onToggle}
      className={cn(
        "group min-h-[8.5rem] rounded-[1rem] border bg-white p-3 text-right shadow-[0_10px_24px_rgba(36,21,26,0.045)] outline-none transition hover:-translate-y-0.5 sm:p-4",
        isSelected
          ? "border-[#ee376a] ring-2 ring-[rgba(238,55,106,0.18)]"
          : "border-[rgba(238,55,106,0.14)] hover:border-[rgba(238,55,106,0.32)]",
      )}
    >
      <span className="flex items-start justify-between gap-3">
        <span
          className={cn(
            "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border",
            isSelected
              ? "border-[#ee376a] bg-[#ee376a] text-white"
              : "border-[rgba(238,55,106,0.18)] bg-[#fff8fb] text-transparent",
          )}
        >
          <Check className="h-4 w-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block break-words text-sm font-black text-[var(--color-ink)]">
            {group.title ?? group.group}
          </span>
          {group.price ? (
            <span className="mt-2 inline-flex items-center rounded-full border border-[rgba(238,55,106,0.28)] bg-[rgba(238,55,106,0.1)] px-3 py-1 text-xs font-black text-[#ee376a] md:text-sm">
              {formatCurrency(group.price)}
            </span>
          ) : null}
        </span>
      </span>

      {visibleItems.length > 0 ? (
        <span className="mt-3 flex flex-wrap gap-1.5">
          {visibleItems.map((item, index) => (
            <span
              key={`${group.group}-selectable-${item.label}-${index}`}
              className="inline-flex max-w-full items-center gap-1 rounded-full bg-[#fff8fb] px-2.5 py-1 text-[11px] font-bold text-[#6f5b63]"
            >
              <span className="shrink-0 text-[#ee376a]">{item.label}:</span>
              {item.type === "color" ? (
                <span className="flex items-center gap-1">
                  {item.colors.slice(0, 3).map((color) => (
                    <span
                      key={color}
                      className="h-3.5 w-3.5 rounded-full border border-white shadow-[0_0_0_1px_rgba(238,55,106,0.18)]"
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </span>
              ) : (
                <span className="min-w-0 truncate">{item.value}</span>
              )}
            </span>
          ))}
        </span>
      ) : null}
    </button>
  );
}
function BuyNowButton({
  variantId,
  isAvailable,
}: {
  variantId: number;
  isAvailable: boolean;
}) {
  const router = useRouter();
  // Buy-now uses the same cart API for guests and signed-in customers.
  const isAuthenticated = true;
  const guestIsInCartSelector = useMemo(
    () => selectIsVariantInCart(variantId),
    [variantId],
  );
  const guestIsInCart = useAppSelector(guestIsInCartSelector);
  const selectAuthCartFlag = useMemo(
    () =>
      createSelector(
        [
          (result: { data?: { items: { variantId: number }[] } }) =>
            result.data?.items ?? [],
        ],
        (items) => items.some((item) => item.variantId === variantId),
      ),
    [variantId],
  );
  const { isInCart: authIsInCart } = cartApi.useGetCartQuery(undefined, {
    skip: !isAuthenticated,
    selectFromResult: (result) => ({
      isInCart: selectAuthCartFlag(result),
    }),
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const { addItem, isMutating, mutationState } = useCart({
    autoLoad: false,
  });
  const [isRedirecting, setIsRedirecting] = useState(false);
  const itemIsInCart = isAuthenticated ? authIsInCart : guestIsInCart;
  const isAddingThisVariant =
    mutationState?.type === "add" && mutationState.variantId === variantId;
  const isLoading = isRedirecting || isMutating || isAddingThisVariant;

  return (
    <button
      type="button"
      disabled={!isAvailable || isLoading}
      className={cn(
        secondaryButtonClass,
        "disabled:cursor-not-allowed disabled:opacity-60",
      )}
      onClick={async () => {
        if (!isAvailable) {
          return;
        }

        setIsRedirecting(true);

        try {
          if (!itemIsInCart) {
            await addItem(variantId);
          }

          router.push("/checkout");
        } finally {
          setIsRedirecting(false);
        }
      }}
    >
      {isLoading ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          جاري التحويل...
        </>
      ) : (
        "اشتري الآن"
      )}
    </button>
  );
}

function formatProductDescription(description: string) {
  const normalizedDescription = description.replace(/\r\n?/g, "\n").trim();

  if (!normalizedDescription) {
    return [];
  }

  if (normalizedDescription.includes("\n")) {
    return normalizedDescription
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);
  }

  return normalizedDescription
    .replace(/([.!?؟؛:])(\s+)/g, "$1\n")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
}
export default function ProductDetailsPanel({
  product,
  isAvailable,
  infoGroups,
  showInfoGroups = true,
  showSelectableGroups = true,
  selectedVariantIdsOverride,
  hasSelectableInfoGroupsOverride,
  isFullProductSelectedOverride,
}: ProductDetailsPanelProps) {
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(true);
  const [isMobileDescriptionExpanded, setIsMobileDescriptionExpanded] = useState(false);
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const selectableInfoGroups = useMemo(
    () =>
      infoGroups.filter(
        (group) => typeof group.variantId === "number" && group.variantId > 0,
      ),
    [infoGroups],
  );
  const selectableGroupIds = useMemo(
    () => selectableInfoGroups.map((group) => group.variantId as number),
    [selectableInfoGroups],
  );
  const selectableGroupKey = selectableGroupIds.join(",");
  const [selectionState, setSelectionState] = useState({
    key: selectableGroupKey,
    ids: selectableGroupIds,
  });
  const selectedGroupIds =
    selectionState.key === selectableGroupKey
      ? selectionState.ids
      : selectableGroupIds;
  const descriptionLines = useMemo(
    () => formatProductDescription(product.description),
    [product.description],
  );
  const isDescriptionCollapsible = descriptionLines.length > 3;
  const hasSelectableInfoGroups = selectableInfoGroups.length > 0;
  const isFullProductSelected =
    hasSelectableInfoGroups &&
    selectedGroupIds.length === selectableGroupIds.length;
  const internalSelectedVariantIds = hasSelectableInfoGroups
    ? isFullProductSelected
      ? [product.variantId]
      : selectedGroupIds
    : [product.variantId];
  const effectiveHasSelectableInfoGroups =
    hasSelectableInfoGroupsOverride ?? hasSelectableInfoGroups;
  const effectiveIsFullProductSelected =
    isFullProductSelectedOverride ?? isFullProductSelected;
  const selectedVariantIds =
    selectedVariantIdsOverride ?? internalSelectedVariantIds;

  const toggleGroupSelection = (variantId: number) => {
    setSelectionState((currentState) => {
      const currentIds =
        currentState.key === selectableGroupKey
          ? currentState.ids
          : selectableGroupIds;

      return {
        key: selectableGroupKey,
        ids: currentIds.includes(variantId)
          ? currentIds.filter((id) => id !== variantId)
          : [...currentIds, variantId],
      };
    });
  };

  const toggleAllGroups = () => {
    setSelectionState((currentState) => {
      const currentIds =
        currentState.key === selectableGroupKey
          ? currentState.ids
          : selectableGroupIds;

      return {
        key: selectableGroupKey,
        ids:
          currentIds.length === selectableGroupIds.length
            ? []
            : selectableGroupIds,
      };
    });
  };

  return (
    <div className="rounded-[1.25rem] border border-[rgba(238,55,106,0.1)] bg-white p-4 text-right shadow-[0_16px_34px_rgba(61,45,33,0.04)] md:rounded-[1.6rem] md:p-6">
      <div className="flex flex-wrap items-center gap-3">
        <span className="inline-flex items-center rounded-full bg-[#fff0f5] px-3 py-1 text-xs font-extrabold text-[#ee376a]">
          {product.categoryLabel}
        </span>
      </div>

      <h1 className="mt-4 break-words text-[1.18rem] font-extrabold leading-[1.45] !text-[#ee376a] md:text-[1.5rem]">
        {product.title}
      </h1>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-sm">
        <div className="flex items-center gap-2 jusify-center">
          <span className="font-bold text-[#f5b301]">
            {product.rating ? product.rating.toFixed(1) : "0.0"}
          </span>
          <div className="flex items-center gap-1">
            {renderStars(product.rating)}
          </div>

          <span className="text-[var(--color-ink-soft)]">
            ({product.reviewsCount} تقييمات)
          </span>
        </div>
        {/* <span className="text-[var(--color-line)]">|</span> */}
        <span className="rounded-full bg-[#ffe6ef] px-2 py-1 font-semibold text-[#ee376a]">
          SKU: {product.sku}
        </span>
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <p className="text-[2.15rem] font-extrabold !text-[#ee376a] md:text-4xl">
          {formatCurrency(product.price)}
        </p>

        {isAuthenticated && product.discountPercentage ? (
          <span className="inline-flex rounded-full bg-[#ffe6ef] px-3 py-1 text-sm font-extrabold text-[#ee376a]">
            {formatDiscountLabel(product.discountPercentage)}
          </span>
        ) : null}
        {product.originalPrice !== product.price ? (
          <p className="text-lg font-bold !text-[#b0b0b0] line-through">
            {formatCurrency(product.originalPrice)}
          </p>
        ) : null}
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-b border-[rgba(36,36,36,0.08)] pb-5">
        <span
          className={cn(
            "text-sm font-bold",
            isAvailable ? "!text-[#24a148]" : "text-[#b6432c]",
          )}
        >
          {isAvailable ? "متوفر في المخزون" : "غير متوفر في المخزون"}
        </span>
        <span className="text-sm !text-[#24a148]">
          {product.stock} قطعة متاحة
        </span>
      </div>

      <div className="mt-2 rounded-[1.2rem] border border-[rgba(37,211,102,0.22)] bg-[rgba(37,211,102,0.08)] p-4">
        <Link
          href={siteConfig.links.whatsapp}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-3 text-right transition hover:opacity-90"
        >
          <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#25d366] !text-white shadow-[0_10px_22px_rgba(37,211,102,0.24)]">
            <BiLogoWhatsapp className="h-5 w-5" />
          </span>
          <div>
            <p className="text-sm font-extrabold text-[#128c7e]">
              لو حابب تغيّر اللون او تعاين بنفسك تواصل معنا على واتساب
            </p>
            {/* <p className="mt-1 text-sm leading-7 text-[var(--color-ink-soft)]">
              ابعت لنا اللون المطلوب وسنساعدك بسرعة في تأكيد التفاصيل المتاحة.
            </p> */}
          </div>
        </Link>
      </div>

      {showSelectableGroups && hasSelectableInfoGroups ? (
        <section className="mt-4 rounded-[1.1rem] border border-[rgba(238,55,106,0.12)] bg-[#fffafb] p-3 sm:p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-sm font-black text-[var(--color-ink)]">
              مكونات الطلب
            </h2>
            <button
              type="button"
              onClick={toggleAllGroups}
              className="inline-flex h-10 items-center justify-center rounded border border-[rgba(238,55,106,0.18)] bg-white px-3 text-xs font-black text-[#ee376a] transition hover:bg-[#fff0f5]"
            >
              {selectedGroupIds.length === selectableGroupIds.length
                ? "إلغاء تحديد الكل"
                : "تحديد الكل"}
            </button>
          </div>

          <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {selectableInfoGroups.map((group) => {
              const variantId = group.variantId as number;

              return (
                <SelectableGroupCard
                  key={`${group.group}-${variantId}`}
                  group={group}
                  isSelected={selectedGroupIds.includes(variantId)}
                  onToggle={() => toggleGroupSelection(variantId)}
                />
              );
            })}
          </div>
        </section>
      ) : null}

      {showInfoGroups ? (
        <div className="mt-4 grid gap-3 border-t border-[rgba(36,36,36,0.08)] pt-4">
          {infoGroups.map((group, index) => (
            <OptionGroupDisplay key={`${group.group}-${index}`} group={group} />
          ))}
        </div>
      ) : null}

      <div className="mt-6 grid gap-3">
        <AddToCartButton
          variantId={
            effectiveHasSelectableInfoGroups
              ? (selectedVariantIds[0] ?? null)
              : product.variantId
          }
          variantIds={
            effectiveHasSelectableInfoGroups ? selectedVariantIds : undefined
          }
          parentVariantId={
            effectiveHasSelectableInfoGroups ? product.variantId : undefined
          }
          isAvailable={
            isAvailable &&
            (!effectiveHasSelectableInfoGroups || selectedVariantIds.length > 0)
          }
          mode={effectiveHasSelectableInfoGroups ? "add" : "toggle"}
          label={
            effectiveHasSelectableInfoGroups
              ? effectiveIsFullProductSelected
                ? "إضافة المنتج كامل للسلة"
                : `إضافة ${selectedVariantIds.length} اختيار للسلة`
              : "أضف إلى السلة"
          }
          redirectToCartOnSuccess
          className={cn(
            primaryButtonClass,
            "hidden border-0 hover:text-white active:text-white lg:inline-flex",
          )}
        />
        <BuyNowButton variantId={product.variantId} isAvailable={isAvailable} />
      </div>

      <div className="mt-2 rounded-[1.15rem] border border-[rgba(238,55,106,0.1)] bg-[#fff8fb] p-3 md:p-4">
        <div
          className={cn(
            "relative overflow-hidden transition-[max-height] duration-300 ease-out",
            isDescriptionExpanded || !isDescriptionCollapsible
              ? "max-h-none"
              : "lg:max-h-[6.7rem]",
          )}
        >
          <ul className="space-y-1.5 text-[0.82rem] font-semibold leading-6 text-[var(--color-ink-soft)] md:text-sm md:leading-7">
            {descriptionLines.map((line, index) => (
              <li
                key={`${product.variantId}-description-${index}`}
                className={cn("items-start gap-2 break-words", index < 2 || isMobileDescriptionExpanded ? "flex" : "hidden lg:flex")}
              >
                <span
                  className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#ee376a]"
                  aria-hidden="true"
                />
                <span className="min-w-0 flex-1">{line}</span>
              </li>
            ))}
          </ul>
          {!isDescriptionExpanded && isDescriptionCollapsible ? (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 hidden h-10 bg-gradient-to-t from-[#fff8fb] to-transparent lg:block" />
          ) : null}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-[var(--color-ink-soft)]">
          {descriptionLines.length > 2 ? (
            <button
              type="button"
              className="inline-flex min-h-11 items-center justify-center rounded bg-[#ee376a] px-3 py-2 text-xs font-extrabold !text-white transition hover:opacity-90 lg:hidden"
              aria-expanded={isMobileDescriptionExpanded}
              onClick={() => setIsMobileDescriptionExpanded((prev) => !prev)}
            >
              {isMobileDescriptionExpanded ? "عرض أقل" : "… عرض المزيد"}
            </button>
          ) : null}
          {isDescriptionCollapsible ? (
            <button
              type="button"
              className="hidden items-center justify-center rounded bg-[#ee376a] px-3 py-2 text-xs font-extrabold !text-white transition hover:opacity-90 lg:inline-flex"
              onClick={() => setIsDescriptionExpanded((prev) => !prev)}
            >
              {isDescriptionExpanded ? "عرض أقل" : "عرض المزيد"}
            </button>
          ) : null}
          {product.isNew ? <span>وصل حديثًا</span> : null}
          <Link
            href="/products"
            className="inline-flex items-center justify-center rounded border border-[rgba(238,55,106,0.16)] bg-white px-3 py-2 text-xs font-extrabold !text-[#ee376a] transition hover:bg-[#fff0f5]"
          >
            تصفح المزيد
          </Link>
        </div>
      </div>
    </div>
  );
}
